// https://angular.io/guide/dynamic-component-loader

import { AnimationEvent } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, Type, ChangeDetectionStrategy } from '@angular/core';
import { FirstPair } from '@infrastructure/firstPair.enum';
import { Gender } from '@infrastructure/gender.enum';
import ValidationResult from '@infrastructure/validation-result';
import { DiscountModel } from '@models/discounts/discount-model';
import { MenuModel } from '@models/menu';
import { MiscModel } from '@models/misc-model';
import { TempleInclinationEnum } from '@models/variant-temple-inclination';
import { MenuService } from '@services/menu.service';
import { StateService } from '@services/state.service';
import { AngleConstants } from '@shared/angle-constants';
import { GrowTransition } from '@shared/transitions/grow.transition';
import { ComponentOutletInjectorModule, DynamicAttributesModule, DynamicComponent, DynamicIoDirective, OutputsType } from 'ng-dynamic-component';
import { ModelModel } from '@models/api-models/model';
import { ComponentVariantModel } from '@models/component-variant';
import { ComponentVariantChangeModel } from '@models/component-variant-change';
import { ConfigurationModel } from '@models/configuration';
import { ConfigurationPlusModel } from '@models/configuration-plus-model';
import { CustomizerMenuPartModel } from '@models/customizer-menu-part';
import { ModelGroupModel } from '@models/model-group';
import { ModelSizeModel } from '@models/model-size';
import { VariantColorModel } from '@models/variant-color';
import { ComponentCodeConstants } from '../../../../shared/component-constants';
import { CustomizerStateConstants } from '../../../../shared/customizer-state-constants';
import { FadeInOutTransition } from '../../../../shared/transitions/fade-in-out.transition';

@Component({
	selector: 'customizer-loader',
	templateUrl: 'customizer-loader.html',
	styleUrls: ['./customizer-loader.scss'],
	animations: [
		FadeInOutTransition(1, 300, 300),
		GrowTransition()
	],
	imports: [DynamicAttributesModule, CommonModule, DynamicIoDirective, ComponentOutletInjectorModule, DynamicComponent // CustomizerModule,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true
})
export class CustomizerComponentLoaderComponent {

	@Input() isLimited: boolean;

	@Input() reference: string;
	@Input() comment: string;
	@Input() hasForms: boolean;
	@Input() hasFixtures: boolean;
	@Input() engraving: string;
	@Input() component?: CustomizerMenuPartModel;
	@Input() configurations: Array<ConfigurationModel>;
	@Input() configuration: ConfigurationModel | null;
	@Input() imageOnlyVariants: Array<ComponentVariantModel>;
	@Input() variants?: Array<ComponentVariantModel>;
	@Input() modelSize?: ModelSizeModel;
	@Input() model?: ModelModel;
	@Input() modelGroup?: ModelGroupModel;
	@Input() isDemo: boolean;
	@Input() state: CustomizerStateConstants;
	@Input() currentVariantConstant: ComponentCodeConstants;
	@Input() discount: DiscountModel | null;
	@Input() discountErrors: ValidationResult;
	@Input() age: string | null;
	@Input() gender: Gender | null;
	@Input() firstPair: FirstPair | null;
	@Input() selectedTempleInclination?: TempleInclinationEnum | null;
	@Input() selectedCase?: MiscModel | null;

	@Output() setImageSliderByName: EventEmitter<AngleConstants> = new EventEmitter();
	@Output() updateSize: EventEmitter<ModelSizeModel> = new EventEmitter();
	@Output() updateModel: EventEmitter<ModelModel> = new EventEmitter();
	@Output() updateConfiguration: EventEmitter<ConfigurationPlusModel> = new EventEmitter();
	@Output() updateVariant: EventEmitter<ComponentVariantChangeModel> = new EventEmitter();
	@Output() updateVariants: EventEmitter<Array<ComponentVariantChangeModel>> = new EventEmitter();
	@Output() updateComponentSize: EventEmitter<ComponentVariantChangeModel> = new EventEmitter();
	@Output() updateVariantDesign: EventEmitter<ComponentVariantChangeModel> = new EventEmitter();
	@Output() updateMenuVisited: EventEmitter<MenuModel> = new EventEmitter();
	@Output() updateCollapsedOnComponent: EventEmitter<boolean> = new EventEmitter();
	@Output() updateReference: EventEmitter<string> = new EventEmitter();
	@Output() updateComment: EventEmitter<string> = new EventEmitter();
	@Output() updateEngraving: EventEmitter<string> = new EventEmitter();
	@Output() matchColors: EventEmitter<VariantColorModel> = new EventEmitter();
	@Output() updateClipOnOnly: EventEmitter<boolean> = new EventEmitter();
	@Output() updateCurrentMainComponent: EventEmitter<CustomizerMenuPartModel> = new EventEmitter();
	@Output() updateHasForms: EventEmitter<boolean> = new EventEmitter();
	@Output() updateHasFixtures: EventEmitter<boolean> = new EventEmitter();
	@Output() updateDiscount: EventEmitter<DiscountModel> = new EventEmitter();

	@Output() updateAge: EventEmitter<string> = new EventEmitter();
	@Output() updateGender: EventEmitter<Gender> = new EventEmitter();
	@Output() updateFirstPair: EventEmitter<FirstPair> = new EventEmitter();
	@Output() updateTempleInclination: EventEmitter<TempleInclinationEnum> = new EventEmitter();
	@Output() updateCase: EventEmitter<MiscModel> = new EventEmitter();
	@Output() updateParts: EventEmitter<string[]> = new EventEmitter();

	public expanded: boolean = false;
	isReady: boolean = false;

	constructor(
		private stateService: StateService,
		public menuService: MenuService
	) {
		stateService.state$.next(null);
	}

	public outputs = {
		setImageSliderByNameChange: (x: AngleConstants) => this.setImageSliderByName.emit(x),
		updateSizeChange: (x: ModelSizeModel) => this.updateSize.emit(x),
		updateConfigurationChange: (x: ConfigurationPlusModel) => this.updateConfiguration.emit(x),
		updateVariantChange: (x: ComponentVariantChangeModel) => this.updateVariant.emit(x),
		updateVariantsChange: (x: Array<ComponentVariantChangeModel>) => this.updateVariants.emit(x),
		updateComponentSizeChange: (x: ComponentVariantChangeModel) => this.updateComponentSize.emit(x),
		updateVariantDesignChange: (x: ComponentVariantChangeModel) => this.updateVariantDesign.emit(x),
		updateMenuVisitedChange: (x: MenuModel) => this.updateMenuVisited.emit(x),
		updateModelChange: (x: ModelModel) => this.updateModel.emit(x),
		updateCollapsedOnComponent: (x: boolean) => this.updateCollapsedOnComponent.emit(x),
		updateReferenceChange: (x: string) => this.updateReference.emit(x),
		updateCommentChange: (x: string) => this.updateComment.emit(x),
		updateEngravingChange: (x: string) => this.updateEngraving.emit(x),
		matchColorsChange: (x: VariantColorModel) => this.matchColors.emit(x),
		setExpandedChange: (x: boolean) => this.expanded = x,
		onlyClipOnChange: (x: boolean) => this.updateClipOnOnly.emit(x),
		currentMainComponentChange: (x: CustomizerMenuPartModel) => this.updateCurrentMainComponent.emit(x),
		updateHasFormsChange: (x: boolean) => this.updateHasForms.emit(x),
		updateHasFixturesChange: (x: boolean) => this.updateHasFixtures.emit(x),
		updateDiscountChange: (x: DiscountModel) => this.updateDiscount.emit(x),
		updateAgeChange: (x: string) => this.updateAge.emit(x),
		updateGenderChange: (x: Gender) => this.updateGender.emit(x),
		updateFirstPairChange: (x: FirstPair) => this.updateFirstPair.emit(x),
		updateTempleInclinationChange: (x: TempleInclinationEnum) => this.updateTempleInclination.emit(x),
		updateCaseChange: (x: MiscModel) => this.updateCase.emit(x),
		updatePartsChange: (x: string[]) => this.updateParts.emit(x)
	} as OutputsType;

	public asAny = (): Type<any> => this.component?.component as any;

	public show(show: AnimationEvent) {
		// Safari among others run the "done" twice, and since this is a toggle, then we make sure it only is set to true, when "done" is called
		this.isReady = show.fromState == "void";
	}

}
